'use client';

import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Header from '@/components/Header';
import HeroImage from '@/components/HeroImage';
import Footer from '@/components/Footer';
import styles from './events.module.css';
import Image from 'next/image';
import { BreadcrumbSchema, EventsListSchema } from '@/lib/schema';


// Event images (same as original)
const eventImages = [
  'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'https://images.unsplash.com/photo-1544552866-d3ed42536cfd?w=800&q=80',
  'https://images.unsplash.com/photo-1551524164-687a55dd1126?w=800&q=80',
  'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80',
  'https://images.unsplash.com/photo-1511994298241-608e28f14fde?w=800&q=80',
  'https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?w=800&q=80',
];

export default function EventsPage() {
  const t = useTranslations('events');
  const tCommon = useTranslations('common');

  // Build events array from translations
  const events = [
    {
      id: 'chile-ski-season',
      title: t('items.chileSkiSeason.title'),
      date: t('items.chileSkiSeason.date'),
      description: t('items.chileSkiSeason.description'),
      category: t('items.chileSkiSeason.category'),
      image: eventImages[0],
    },
    {
      id: 'patagonia-fly-fishing',
      title: t('items.patagoniaFlyFishing.title'),
      date: t('items.patagoniaFlyFishing.date'),
      description: t('items.patagoniaFlyFishing.description'),
      category: t('items.patagoniaFlyFishing.category'),
      image: eventImages[1],
    },
    {
      id: 'heli-skiing-week',
      title: t('items.heliSkiingWeek.title'),
      date: t('items.heliSkiingWeek.date'),
      description: t('items.heliSkiingWeek.description'),
      category: t('items.heliSkiingWeek.category'),
      image: eventImages[2],
    },
    {
      id: 'wellness-yoga-retreat',
      title: t('items.wellnessYogaRetreat.title'),
      date: t('items.wellnessYogaRetreat.date'),
      description: t('items.wellnessYogaRetreat.description'),
      category: t('items.wellnessYogaRetreat.category'),
      image: eventImages[3],
    },
    {
      id: 'mountain-biking-festival',
      title: t('items.mountainBikingFestival.title'),
      date: t('items.mountainBikingFestival.date'),
      description: t('items.mountainBikingFestival.description'),
      category: t('items.mountainBikingFestival.category'),
      image: eventImages[4],
    },
    {
      id: 'photography-expedition',
      title: t('items.photographyExpedition.title'),
      date: t('items.photographyExpedition.date'),
      description: t('items.photographyExpedition.description'),
      category: t('items.photographyExpedition.category'),
      image: eventImages[5],
    },
  ];

  return (
    <div className={styles.pageWrapper}>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: '/' },
        { name: tCommon('events'), href: '/events/' },
      ]} />
      <EventsListSchema events={events} />
      <Header />

      <HeroImage
        backgroundImage="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920&q=80"
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        location={t('hero.location')}
      />

      <main>
        <section className={styles.eventsSection}>
          <div className={styles.container}>
            {/* Section Intro */}
            <motion.div
              className={styles.sectionIntro}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h1>{t('intro.title')}</h1>
              <p>{t('intro.description')}</p>
            </motion.div>

            {/* Events Grid */}
            <div className={styles.eventsGrid}>
              {events.map((event, index) => (
                <motion.article
                  key={event.id}
                  className={styles.eventCard}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className={styles.cardImage}>
                    <Image
                      src={event.image}
                      alt={`${event.title} - ${event.category} event scheduled for ${event.date}`}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      style={{ objectFit: 'cover' }}
                    />
                    <div className={styles.eventCategory}>{event.category}</div>
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.eventDate}>{event.date}</div>
                    <h3>{event.title}</h3>
                    <p>{event.description}</p>
                    <Link href="/contact/" className={styles.btnPrimary}>
                      {t('reserveSpot')}
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>

            {/* CTA Section */}
            <motion.div
              className={styles.sectionCta}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3>{t('cta.title')}</h3>
              <p>{t('cta.description')}</p>
              <Link href="/contact/" className={styles.btnSecondary}>
                {tCommon('contactUs')}
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
